import { PageLoader } from '@/components/PageLoader';
import { useMemo, useState } from 'react';

import type { CatalogueDesign } from '@/api/masters';
import { Button, Card, EmptyState, Notice, SectionTitle } from '@/components/PageHeading';
import { useDebounced } from '@/components/useDebounced';
import { claimedOut, visibleDesigns } from '@core/bookings/catalogue';
import { cardSizes } from '@core/bookings/looksRail';
import { categoriesOnRail, freeToPick, type AvailableUnit } from '@core/bookings/rail';
import { railScan } from '@core/bookings/railScan';
import { withReference } from '@core/units/referenceNumber';

import { DesignCard } from '../catalogue/DesignCard';
import { DesignModal } from '../catalogue/DesignModal';
import { CategoryChip } from './GarmentPicker';
import { ScanInput } from './ScanBox';

/**
 * The booking counter's rail as design cards — one card per design with its photograph, what is
 * free on the dates and the price for the charged days — the proposal's step 3 on the website.
 *
 * <p>
 * <strong>Book by design and size; one garment is still booked.</strong> A card opens the design
 * popup in pick mode: the garment is chosen automatically (free, best condition, rented least) and
 * the clerk may tap another, then **Add for …** puts that exact garment on the wearer. A scan or a
 * typed tag adds the exact garment straight away, since the clerk is holding it.
 * </p>
 *
 * <p>
 * The garment-by-garment rail (`GarmentPicker`) stays one click away for a storeman who wants it —
 * the caller owns the switch. Both read the same answer, so they cannot disagree about what is free.
 * </p>
 */
export function DesignRail({
  designs,
  units,
  days,
  currency,
  claimed,
  forName,
  searching = false,
  searched = false,
  disabled = false,
  notice,
  toggle,
  more,
  replacing = null,
  openId: openIdProp,
  onOpenChange,
  onAdd,
  query,
  mine,
  onRemove,
}: {
  /** The catalogue for the possession dates. The caller owns the query. */
  designs: CatalogueDesign[];
  /** The same answer flattened to garments — what a scan is read against. */
  units: AvailableUnit[];
  /** Charged days — what a card's price is read for. */
  days: number;
  currency: string;
  claimed: ReadonlySet<number>;
  /** Who an Add goes to; null while nobody in the party is chosen. */
  forName: string | null;
  searching?: boolean;
  searched?: boolean;
  disabled?: boolean;
  notice?: React.ReactNode;
  /** The switch to the garment-by-garment view. */
  toggle?: React.ReactNode;
  /**
   * What a search above the rail has typed — the new booking's `GarmentSearch`, which also reads
   * scans. Given, the rail's own search box and scan box step aside.
   */
  query?: string | undefined;
  /**
   * The chosen person's garments. Given with `onRemove`, each card carries its sizes as buttons:
   * one click adds that size for them, a size of theirs takes it back out (`cardSizes`, the phone's
   * rule) — the card itself still opens the design for a colour or a particular garment.
   */
  mine?: ReadonlySet<number> | undefined;
  onRemove?: ((unitId: number) => void) | undefined;
  /** Said under the grid when the catalogue holds more designs than one answer carries. */
  more?: string | null | undefined;
  /**
   * Set while a garment in the bag is being swapped: the next pick replaces it rather than adding.
   * The rail says so above the cards, and the design page's button says "Swap … for this".
   * `sentence` replaces what the notice says the swap keeps — a booking already taken does not
   * carry the old garment's accessories across, a booking being taken does.
   */
  replacing?: { serialCode: string; sentence?: string; onCancel: () => void } | null;
  /** The open design, when the caller drives it — the garment sheet opens its own design. */
  openId?: number | null;
  onOpenChange?: (itemId: number | null) => void;
  onAdd: (unit: AvailableUnit) => void;
}) {
  const [ownSearch, setSearch] = useState('');
  const search = query ?? ownSearch;
  const searchHere = query === undefined;
  const [category, setCategory] = useState<number | null>(null);
  const [ownOpenId, setOwnOpenId] = useState<number | null>(null);
  const openId = openIdProp === undefined ? ownOpenId : openIdProp;
  const setOpenId = onOpenChange ?? setOwnOpenId;
  const debounced = useDebounced(search);

  const categories = useMemo(() => categoriesOnRail(units, claimed), [units, claimed]);
  const visible = useMemo(
    () => visibleDesigns(designs, { category, query: debounced }),
    [designs, category, debounced],
  );
  const open = designs.find((design) => design.itemId === openId) ?? null;
  const byId = useMemo(() => new Map(units.map((unit) => [unit.unitId, unit])), [units]);

  const add = (unitId: number) => {
    const unit = byId.get(unitId);

    if (unit) {
      onAdd(unit);
    }
  };

  return (
    <Card hue="orange">
      <SectionTitle
        icon="icon icon-shirt"
        hue="orange"
        trailing={
          <div className="flex flex-wrap items-center gap-2 max-sm:w-full">
            {searchHere && (
              <div className="relative w-56 max-sm:w-full">
                <i className="icon icon-search text-default pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-[13px]" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Design or garment code"
                  aria-label="Find a design or a garment code"
                  className="border-border-color h-8 w-full rounded border ps-8 pe-2.5 text-[12.5px]"
                />
              </div>
            )}
            {toggle}
          </div>
        }
      >
        Pick the looks
      </SectionTitle>

      {searchHere && (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          {/* The clerk is holding this garment, so a scan books exactly it — no design page. */}
          <ScanInput
            placeholder="Scan a tag to add that garment"
            disabled={disabled}
            onScan={(text) => {
              const result = railScan(text, units);

              if (result === null) {
                return null;
              }

              if (result.tone === 'bad') {
                return result;
              }

              if (claimed.has(result.unit.unitId)) {
                return {
                  tone: 'bad',
                  message: `${result.unit.serialCode} is already in this booking.`,
                };
              }

              if (forName === null) {
                return { tone: 'bad', message: 'Choose who it is for first, then scan again.' };
              }

              onAdd(result.unit);

              return {
                tone: 'good',
                message: replacing
                  ? `${replacing.serialCode} swapped for ${withReference(result.unit.serialCode, result.unit.referenceNumber)}.`
                  : `${withReference(result.unit.serialCode, result.unit.referenceNumber)} added for ${forName}.`,
              };
            }}
          />
        </div>
      )}

      {categories.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          <CategoryChip
            label="All"
            free={freeToPick(units, claimed)}
            total={units.length}
            active={category === null}
            onClick={() => setCategory(null)}
          />
          {categories.map((row) => (
            <CategoryChip
              key={row.id ?? 'none'}
              label={row.name}
              free={row.free}
              total={row.total}
              active={category === row.id}
              onClick={() => setCategory(category === row.id ? null : row.id)}
            />
          ))}
        </div>
      )}

      {replacing ? (
        <div className="mb-3">
          <Notice tone="warning" title={`Swapping ${replacing.serialCode}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {replacing.sentence ??
                  'Pick a design, or scan a tag — whatever is chosen next takes its place, for the same person and with the same accessories.'}
              </span>
              <Button onClick={replacing.onCancel}>Keep it</Button>
            </div>
          </Notice>
        </div>
      ) : (
        notice
      )}

      {searching && designs.length === 0 && <PageLoader compact label="Loading the catalogue" />}

      {!searching && visible.length === 0 && (
        <EmptyState
          icon="icon icon-shirt"
          title={
            !searched
              ? 'Set the dates first'
              : designs.length === 0
                ? 'Nothing in stock here'
                : 'No design matches that'
          }
          hint={
            searched
              ? 'Only designs with a garment on this branch’s rail are shown.'
              : 'Availability is answered for the possession period, as soon as both dates are set.'
          }
        />
      )}

      {visible.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xxl:grid-cols-4">
          {visible.map((design) => (
            <DesignCard
              key={design.itemId}
              // Garments already in this booking are not free to add again, so the card says so.
              design={{ ...design, units: [...claimedOut(design.units, claimed)] }}
              days={days}
              currency={currency || '₹'}
              selected={design.itemId === openId}
              onOpen={() => setOpenId(design.itemId)}
              footer={
                mine && onRemove ? (
                  <SizeButtons
                    design={design}
                    days={days}
                    claimed={claimed}
                    mine={mine}
                    forName={forName}
                    disabled={disabled}
                    onAdd={add}
                    onRemove={onRemove}
                    onOpen={() => setOpenId(design.itemId)}
                  />
                ) : undefined
              }
            />
          ))}
        </div>
      )}

      {more && <p className="text-default mt-3 mb-0 text-[12.5px]">{more}</p>}

      {open && (
        <DesignModal
          key={open.itemId}
          design={open}
          days={days}
          currency={currency || '₹'}
          onClose={() => setOpenId(null)}
          pick={{
            forName,
            claimed,
            disabled,
            actionLabel: replacing ? `Swap ${replacing.serialCode} for this` : undefined,
            onAdd: (unitId) => {
              add(unitId);
              setOpenId(null);
            },
          }}
        />
      )}
    </Card>
  );
}

/** A card's sizes as buttons for the chosen person — or "Choose colour" where a size cannot say. */
function SizeButtons({
  design,
  days,
  claimed,
  mine,
  forName,
  disabled,
  onAdd,
  onRemove,
  onOpen,
}: {
  design: CatalogueDesign;
  days: number;
  claimed: ReadonlySet<number>;
  mine: ReadonlySet<number>;
  forName: string | null;
  disabled: boolean;
  onAdd: (unitId: number) => void;
  onRemove: (unitId: number) => void;
  onOpen: () => void;
}) {
  const { needsLook, sizes } = cardSizes(design, days, claimed, mine, forName ?? 'them');

  if (needsLook) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="border-border-color h-9 w-full cursor-pointer rounded-lg border bg-white text-[13px] font-semibold text-gray-900 hover:bg-light"
      >
        Choose colour
      </button>
    );
  }

  return (
    <ul className="m-0 flex list-none flex-wrap gap-1.5 ps-0">
      {sizes.map((size) => (
        <li key={size.key} className="list-none">
          <button
            type="button"
            disabled={
              disabled || size.state === 'out' || (size.state === 'free' && forName === null)
            }
            aria-pressed={size.state === 'mine'}
            onClick={() => {
              if (size.state === 'mine' && size.removes !== null) {
                onRemove(size.removes);
              } else if (size.adds) {
                onAdd(size.adds.unitId);
              }
            }}
            className={`h-9 cursor-pointer rounded-lg border px-3 text-[13px] disabled:cursor-not-allowed ${
              size.state === 'mine'
                ? 'border-primary bg-primary font-semibold text-white'
                : size.state === 'free'
                  ? 'border-primary/40 text-primary hover:bg-primary-transparent bg-white font-semibold'
                  : 'border-border-color bg-light text-default'
            }`}
          >
            {size.label} · {size.state === 'mine' ? `${size.line} ✓` : size.line}
          </button>
        </li>
      ))}
    </ul>
  );
}
